import { action } from "typesafe-actions";
import { ThunkAction } from "redux-thunk";
import { AnyAction } from "redux";
import {
  ICustomerPolicyDetails,
  ICustomerPolicyDetailsResponse,
} from "./customerPolicyDetails.types";
import { IStoreState } from "../initialStoreState";
import { makeApiCall } from "../../helpers/postRequest";
import { api } from "../../api/api";
import { ERROR_MESSAGES, LoadState } from "../../constants/enums";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";

export const FETCH_CUSTOMER_POLICY_DETAILS_PROGRESS =
  "FETCH_CUSTOMER_POLICY_DETAILS_PROGRESS";
export const FETCH_CUSTOMER_POLICY_DETAILS_SUCCESS =
  "FETCH_CUSTOMER_POLICY_DETAILS_SUCCESS";
export const FETCH_CUSTOMER_POLICY_DETAILS_FAILED =
  "FETCH_CUSTOMER_POLICY_DETAILS_FAILED";

export const fetchCustomerPolicyDetailsProgress = () =>
  action(FETCH_CUSTOMER_POLICY_DETAILS_PROGRESS);

export const fetchCustomerPolicyDetailsSuccess = (
  details: ICustomerPolicyDetails,
  customerPolicyId: number,
  isNewPolicy: boolean
) =>
  action(FETCH_CUSTOMER_POLICY_DETAILS_SUCCESS, {
    details,
    customerPolicyId,
    isNewPolicy,
  });

export const fetchCustomerPolicyDetailsFailed = (errorMessage: string) =>
  action(FETCH_CUSTOMER_POLICY_DETAILS_FAILED, { errorMessage });

export const CLEAR_CUSTOMER_POLICY_DETAILS_STATE =
  "CLEAR_CUSTOMER_POLICY_DETAILS_STATE";

export const clearCustomerPolicyDetailsState = () =>
  action(CLEAR_CUSTOMER_POLICY_DETAILS_STATE);

/**
 * Merged save thunk. Persists customer info, policy admin, and premium together
 * through the single Merged_Save_Endpoint. Replaces the three prior per-section
 * save thunks for the customer-info section of every policy type.
 *
 * The payload is already in canonical field names (source-level rename), so there
 * is no boundary transform. `policy_sub_type` carries the sub-type NAME (the value
 * bound by the Line-of-Business dropdown), while `policy_id` must carry the
 * sub-type ID. The id is resolved from the policies master list by matching the
 * selected line of business and sub-type name. The `initialData` vs payload diff
 * is preserved via `makeApiCall`'s automation so the workflow-initiate diff
 * behaves exactly as before.
 */
export const saveCustomerPolicyDetailsAsync =
  (
    data: ICustomerPolicyDetails,
    initialData: ICustomerPolicyDetails,
    isUpdate: boolean,
    onCallback: (isSuccess: boolean, policyId?: number, error?: any) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());

      // Resolve the sub-type ID from the master policies list by matching the
      // selected line of business and the sub-type NAME held in policy_sub_type.
      const policiesList = getState().common.policies.list;
      const matchedLineOfBusiness = policiesList.find(
        (policy) => policy.type === data.line_of_business
      );
      const matchedSubType = matchedLineOfBusiness?.subTypes?.find(
        (subType) => subType.name === data.policy_sub_type
      );
      const resolvedPolicyId = matchedSubType?.policy_id ?? data.policy_id;

      const res = await makeApiCall(
        {
          url: "/policy/edit-all-customer-policy-details",
          method: "POST",
          automation: {
            isUpdate: isUpdate,
            primaryFieldName: "customer_policy_id",
          },
        },
        initialData,
        {
          ...data,
          // policy_id carries the sub-type ID; policy_sub_type (sent via ...data)
          // carries the sub-type NAME.
          policy_id: resolvedPolicyId,
          // Send policy_type as the line of business selected in the UI, for
          // every policy type (Automobile, Commercial Auto, Business,
          // Habitational, Life Insurance).
          policy_type: data.line_of_business,
          effective_time: data.effective_time,
        }
      );

      const customerPolicyId = res.data.data.customer_policy_id;

      dispatch(
        fetchCustomerPolicyDetailsSuccess(res.data.data, customerPolicyId, true)
      );
      onCallback(true, customerPolicyId);
    } catch (err: any) {
      onCallback(false, undefined, err);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

/**
 * Merged fetch thunk. Retrieves the merged customer info, policy admin, and
 * premium data for a `customer_policy_id` through the single Merged_Fetch_Endpoint.
 * Replaces the three prior per-section fetch thunks for the customer-info section
 * of every policy type.
 *
 * The GET response is already in canonical field names, so the first record is
 * stored as-is. When editing an existing record the fetched policy is not new,
 * so `isNewPolicy` is dispatched as `false`.
 */
export const fetchCustomerPolicyDetailsAsync =
  (
    customerPolicyId: number,
    reload?: boolean
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    const currentState = getState().customerPolicyDetails;
    // Only treat the store as "already saved" when the loaded record belongs to
    // the SAME customer policy being requested. Switching to a different
    // customer/policy must always re-fetch, otherwise the previous customer's
    // details stay cached until a hard refresh ("stale until refresh" bug).
    const isSamePolicyLoaded =
      currentState.details.loading === LoadState.Loaded &&
      currentState.customerPolicyId === customerPolicyId;

    try {
      if (!isSamePolicyLoaded || reload) {
        dispatch(fetchCustomerPolicyDetailsProgress());
        const res = await api.get(
          `/policy/get-all-customer-policy-details?customer_policy_id=${customerPolicyId}`
        );
        const responseData: ICustomerPolicyDetailsResponse = res.data;
        const data = responseData.data;
        if (data.length > 0) {
          dispatch(
            fetchCustomerPolicyDetailsSuccess(
              {
                ...data[0],
              },
              customerPolicyId,
              false
            )
          );
        } else {
          dispatch(
            fetchCustomerPolicyDetailsFailed(ERROR_MESSAGES.POLICY_NOT_FOUND)
          );
        }
      }
    } catch (err: any) {
      dispatch(fetchCustomerPolicyDetailsFailed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };
